/**
 * Where every treat sits on the Menu (specs/cat-mouse-mode.md §3.2, and each mode's own §3.2).
 *
 * This used to be seven `const slots = […]` literals buried in seven `MenuScene.addX` methods, and
 * the only check that they did not overlap was reading them. They did overlap: the Cat shipped into
 * `(1190, 398)`, `(90, 398)` and `(345, 480)` — the Satellite's second slot, the Galaxy's second,
 * and the Plate's third — behind a code comment confidently asserting all three were free. It was
 * only visible with several banks full at once, which is a state you have to *earn* to see.
 *
 * So the layout is data now, in one place, and `menuSlots.test.ts` walks every pair of treats at
 * full caps and fails on any shared position. A collision is arithmetic; it should not need eyes.
 *
 * Phaser-free on purpose — this is a table, and the test must not need a canvas to read it.
 */
import { GAME_WIDTH } from '@/game/config';
import {
  UFO_MAX, DIAMOND_MAX, ICE_MAX, SAT_MAX, GALAXY_MAX, PASTA_MAX, CAT_MAX, WORM_MAX, ROCKET_MAX,
} from '@/game/progression/config';

export interface MenuSlot {
  x: number;
  y: number;
}

export type TreatKind =
  'ufo' | 'diamond' | 'cube' | 'galaxy' | 'satellite' | 'plate' | 'cat' | 'worm' | 'rocket';

/** The five rows the menu's treat columns use, top to bottom. */
const ROWS = { r1: 150, gapA: 233, r2: 315, gapB: 398, r3: 480 };
/** The columns, outermost first. `mirror` is the same column on the other side. */
const COL = { outer: 90, ufo: 220, inner: 345 };
const mirror = (x: number): number => GAME_WIDTH - x;

const pair = (x: number, y: number): MenuSlot[] => [{ x, y }, { x: mirror(x), y }];

/**
 * Filled in order, so a bank of one is balanced and a bank of four is a shape. Every list is at
 * least as long as its cap — a treat with nowhere to go is a treat that silently does not appear.
 */
export const TREAT_SLOTS: Record<TreatKind, MenuSlot[]> = {
  // The saucers own the middle column's three rows.
  ufo: [...pair(COL.ufo, ROWS.r1), ...pair(COL.ufo, ROWS.r2), ...pair(COL.ufo, ROWS.r3)],
  // The gems interleave one column further in.
  diamond: [...pair(COL.inner, ROWS.gapA), ...pair(COL.inner, ROWS.gapB)],
  // The cubes own the outer column's three rows.
  cube: [...pair(COL.outer, ROWS.r1), ...pair(COL.outer, ROWS.r2), ...pair(COL.outer, ROWS.r3)],
  // Galaxy and Satellite face each other across the bottom — the two ends of the ladder.
  galaxy: [{ x: COL.outer, y: ROWS.r3 }, { x: COL.outer, y: ROWS.gapB }, { x: COL.ufo, y: ROWS.r3 }],
  satellite: [
    { x: mirror(COL.outer), y: ROWS.r3 },
    { x: mirror(COL.outer), y: ROWS.gapB },
    { x: mirror(COL.ufo), y: ROWS.r3 },
  ],
  // The plates take the outer column's upper gap, then the inner column's bottom row.
  plate: [...pair(COL.outer, ROWS.gapA), { x: COL.inner, y: ROWS.r3 }],
  /**
   * The cats interleave the SAUCER column, the way the diamonds interleave the inner one: the two
   * gaps at x 220 / 1060, which the UFOs (rows 1–3) and the Galaxy and Satellite (row 3) leave
   * empty. Four slots, symmetric, and the last four positions on this menu that nothing else wants.
   */
  cat: [...pair(COL.ufo, ROWS.gapA), ...pair(COL.ufo, ROWS.gapB)],
  /**
   * The worms take the INNER column's top row — the highest pair on the menu, for the highest rung
   * on the ladder (specs/space-worm-mode.md §3.2). The diamonds interleave that column's gaps and
   * the plate takes its bottom row; rows 1 and 2 were never claimed by anything.
   *
   * Two, not four, so `(345, 315)` / `(935, 315)` stayed free for a ninth treat kind — the Rocket.
   */
  worm: [...pair(COL.inner, ROWS.r1)],
  /**
   * The rockets take the inner column's SECOND row — the last symmetric pair on the menu, which
   * the Worm left free for exactly this (specs/rocket-read-mode.md §3.2). At 29 buttons the only
   * position left is `(935, 480)`, the Plate's unclaimed fourth.
   */
  rocket: [...pair(COL.inner, ROWS.r2)],
};

/** The display cap for each treat — how many of its slots can ever be filled at once. */
export const TREAT_CAPS: Record<TreatKind, number> = {
  ufo: UFO_MAX,
  diamond: DIAMOND_MAX,
  cube: ICE_MAX,
  galaxy: GALAXY_MAX,
  satellite: SAT_MAX,
  plate: PASTA_MAX,
  cat: CAT_MAX,
  worm: WORM_MAX,
  rocket: ROCKET_MAX,
};

/** The positions a treat can actually occupy: its slots, capped. */
export function slotsFor(kind: TreatKind): MenuSlot[] {
  return TREAT_SLOTS[kind].slice(0, TREAT_CAPS[kind]);
}

/**
 * The day-streak chip's rectangle (specs/day-streak.md §5). Lives here, Phaser-free, so
 * `menuSlots.test.ts` can prove no treat can ever land under it. `CHIP_MAX_W` is the widest it gets
 * (a 3-digit streak); the chip is right-aligned to `CHIP_RIGHT`, left of the ℹ️ glyph.
 */
export const CHIP_RIGHT = GAME_WIDTH - 70;
export const CHIP_TOP = 12;
export const CHIP_H = 56;
export const CHIP_MAX_W = 170;
export const STREAK_CHIP_RECT = {
  x: CHIP_RIGHT - CHIP_MAX_W, y: CHIP_TOP, w: CHIP_MAX_W, h: CHIP_H,
} as const;
